import React, { useEffect, useState } from "react";

import apiClient from "../../../services/apiClient";
import "./LevelTestMonitor.css";

// 운영자용 레벨테스트 모니터링. 보는 것만 한다.
//
// 응시 현황과 응시코드가 어떻게 돌아가는지 훑어보는 용도라 발급·삭제 같은 쓰기
// 동작은 넣지 않았다. 그런 것은 지금도 GS 쪽 관리자 화면에서 한다.
const TAB_LIST = [
  { key: "academy", text: "학원별 응시" },
  { key: "roster", text: "학원별 재원생" },
  { key: "online", text: "온라인 응시" },
  { key: "code", text: "응시코드" },
];

// 학생이 이달 코드를 못 받은 이유는 두 가지고, 해야 할 일이 서로 다르다.
// '이미 사용' 은 그냥 두면 되고, '코드 부족' 은 발급을 늘려야 한다.
const CODE_STATUS_TEXT = {
  used: "이미 사용",
  short: "코드 부족",
};

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  return `${date.getMonth() + 1}/${date.getDate()}`;
};

const LevelTestMonitor = () => {
  const [tab, setTab] = useState("academy");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  // 재원생 목록은 학원 17곳의 학생을 전부 담아 응시 현황보다 무겁다. 탭을 열 때
  // 한 번만 불러온다.
  const [roster, setRoster] = useState(null);
  const [rosterError, setRosterError] = useState("");
  const [pickedAcademy, setPickedAcademy] = useState("");

  useEffect(() => {
    if (tab !== "roster" || roster || rosterError) {
      return;
    }

    let isCurrent = true;
    apiClient
      .get("/api/v1/echo/admin/roster", { withAuth: true })
      .then((response) => {
        if (isCurrent) {
          setRoster(response.data);
        }
      })
      .catch(() => {
        if (isCurrent) {
          setRosterError("재원생 현황을 불러오지 못했습니다.");
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [tab, roster, rosterError]);

  useEffect(() => {
    let isCurrent = true;
    apiClient
      .get("/api/v1/echo/admin/results", { withAuth: true })
      .then((response) => {
        if (isCurrent) {
          setData(response.data);
        }
      })
      .catch(() => {
        if (isCurrent) {
          setError("레벨테스트 현황을 불러오지 못했습니다.");
        }
      });

    return () => {
      isCurrent = false;
    };
  }, []);

  if (error) {
    return <p className="lt-monitor__empty">{error}</p>;
  }

  if (!data) {
    return null;
  }

  const { summary, code_summary: codeSummary } = data;

  return (
    <section className="lt-monitor">
      <h3 className="lt-monitor__title">레벨테스트 현황</h3>

      <div className="lt-monitor__stats">
        <div className="lt-stat">
          <i>전체 응시</i>
          <b>{summary.total}</b>
        </div>
        <div className="lt-stat">
          <i>완료</i>
          <b>{summary.completed}</b>
        </div>
        <div className="lt-stat">
          <i>리포트</i>
          <b>{summary.report}</b>
        </div>
        <div className="lt-stat">
          <i>온라인 / 학원</i>
          <b>
            {summary.online} / {summary.academy}
          </b>
        </div>
        <div className="lt-stat">
          <i>응시코드 발급</i>
          <b>{codeSummary.total.toLocaleString()}</b>
        </div>
        <div className="lt-stat">
          <i>코드 사용 / 잔여</i>
          <b>
            {codeSummary.used} / {codeSummary.left.toLocaleString()}
          </b>
        </div>
      </div>

      <div className="lt-monitor__tabs">
        {TAB_LIST.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`lt-monitor__tab${tab === item.key ? " is-on" : ""}`}
            onClick={() => setTab(item.key)}
          >
            {item.text}
          </button>
        ))}
      </div>

      <div className="lt-monitor__card">
        <div className="lt-monitor__scroll">
          {tab === "academy" && (
            <table className="lt-monitor__table">
              <tbody>
                <tr className="lt-monitor__head">
                  <td>학원</td>
                  <td className="is-center">응시</td>
                  <td className="is-center">완료</td>
                  <td className="is-center">완료율</td>
                  <td className="is-center">최근 응시</td>
                </tr>
                {data.academies.map((row) => (
                  <tr key={row.academy}>
                    <td className="is-strong">{row.academy}</td>
                    <td className="is-center">{row.total}</td>
                    <td className="is-center">{row.completed}</td>
                    <td className="is-center">
                      {row.total
                        ? `${Math.round((row.completed / row.total) * 100)}%`
                        : "-"}
                    </td>
                    <td className="is-center">
                      {formatDateTime(row.last_started)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {tab === "roster" && (
            <table className="lt-monitor__table">
              <tbody>
                <tr className="lt-monitor__head">
                  <td>학원</td>
                  <td className="is-center">재원생 / 정원</td>
                  <td className="is-center">이달 배정</td>
                  <td className="is-center">이미 사용</td>
                  <td className="is-center">코드 없음</td>
                  <td className="is-center">레테 연결</td>
                  <td className="is-center">남은 코드</td>
                </tr>
                {(roster?.academies || []).map((row) => (
                  <tr
                    key={row.nickname}
                    className={`lt-monitor__pick${
                      pickedAcademy === row.academy ? " is-picked" : ""
                    }`}
                    onClick={() =>
                      setPickedAcademy(
                        pickedAcademy === row.academy ? "" : row.academy
                      )
                    }
                  >
                    <td className="is-strong">
                      {row.academy}
                      {!row.has_account && (
                        <span className="lt-monitor__badge is-warn">
                          계정 없음
                        </span>
                      )}
                    </td>
                    <td className="is-center">
                      {/* 재원생이 정원을 넘으면 더 받을 수 있다는 뜻이다. */}
                      {row.student_count > row.capacity ? (
                        <b className="lt-monitor__warn">{row.student_count}</b>
                      ) : (
                        row.student_count
                      )}
                      <span className="is-muted"> / {row.capacity}</span>
                    </td>
                    <td className="is-center">{row.assigned_count}</td>
                    <td className="is-center">
                      {row.used_count || <span className="is-muted">0</span>}
                    </td>
                    <td className="is-center">
                      {row.short_count ? (
                        <b className="lt-monitor__warn">{row.short_count}</b>
                      ) : (
                        <span className="is-muted">0</span>
                      )}
                    </td>
                    <td className="is-center">
                      {row.linked_count ? (
                        row.linked_count
                      ) : (
                        <b className="lt-monitor__warn">0</b>
                      )}
                    </td>
                    <td className="is-center">
                      {row.code_free}
                      <span className="is-muted"> / {row.code_left}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {tab === "online" && (
            <table className="lt-monitor__table">
              <tbody>
                <tr className="lt-monitor__head">
                  <td>이름</td>
                  <td>학년</td>
                  <td>이메일</td>
                  <td className="is-center">레벨</td>
                  <td className="is-center">점수</td>
                  <td className="is-center">응시일</td>
                </tr>
                {data.online.map((row) => (
                  <tr key={row.session_id}>
                    <td className="is-strong">{row.name}</td>
                    <td>{row.grade}</td>
                    <td className="lt-monitor__email">{row.email || "-"}</td>
                    <td className="is-center">
                      S{String(row.stage).padStart(2, "0")}
                    </td>
                    <td className="is-center">
                      {row.score_percent === null ? (
                        <span className="is-muted">진행중</span>
                      ) : (
                        <b>{row.score_percent}%</b>
                      )}
                    </td>
                    <td className="is-center">{formatDateTime(row.started_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {tab === "code" && (
            <table className="lt-monitor__table">
              <tbody>
                <tr className="lt-monitor__head">
                  <td>응시코드</td>
                  <td>메모</td>
                  <td className="is-center">상태</td>
                  <td className="is-center">만료</td>
                </tr>
                {data.codes.map((row) => (
                  <tr key={row.code}>
                    <td className="lt-monitor__code">{row.code}</td>
                    <td>{row.memo || "-"}</td>
                    <td className="is-center">
                      {row.is_used ? (
                        <span className="lt-monitor__badge">사용완료</span>
                      ) : (
                        <span className="is-muted">미사용</span>
                      )}
                    </td>
                    <td className="is-center">{row.expires || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {tab === "roster" && (
        <>
          {rosterError && <p className="lt-monitor__empty">{rosterError}</p>}
          {!roster && !rosterError && (
            <p className="lt-monitor__note">불러오는 중입니다.</p>
          )}
          {roster && !pickedAcademy && (
            <p className="lt-monitor__note">
              {roster.month} 기준입니다. 학원을 누르면 학생 한 명씩 어떤 코드를
              받았는지 봅니다. '레테 연결' 이 0 이면 학원 화면의 재원생 목록이
              비어 있어 선생님은 코드를 못 봅니다.
            </p>
          )}

          {roster && pickedAcademy && (
            <>
              <h4 className="lt-monitor__subtitle">
                {pickedAcademy} 재원생
              </h4>
              <div className="lt-monitor__card">
                <div className="lt-monitor__scroll">
                  <table className="lt-monitor__table">
                    <tbody>
                      <tr className="lt-monitor__head">
                        <td>이름</td>
                        <td className="is-center">학년</td>
                        <td className="is-center">이달 응시코드</td>
                        <td className="is-center">레테 연결</td>
                      </tr>
                      {(
                        roster.academies.find(
                          (row) => row.academy === pickedAcademy
                        )?.students || []
                      ).map((student) => (
                        <tr key={student.student_id}>
                          <td className="is-strong">{student.name}</td>
                          <td className="is-center">{student.grade}학년</td>
                          <td className="is-center">
                            {student.month_code ? (
                              <span className="lt-monitor__code">
                                {student.month_code}
                              </span>
                            ) : (
                              <span
                                className={
                                  student.code_status === "short"
                                    ? "lt-monitor__warn"
                                    : "is-muted"
                                }
                              >
                                {CODE_STATUS_TEXT[student.code_status]}
                              </span>
                            )}
                          </td>
                          <td className="is-center">
                            {student.is_linked ? (
                              "연결됨"
                            ) : (
                              <span className="is-muted">없음</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {tab === "code" && (
        <p className="lt-monitor__note">
          발급 {codeSummary.total.toLocaleString()}개 중 최근 200개만 보여줍니다.
        </p>
      )}
    </section>
  );
};

export default LevelTestMonitor;
